// Helpers for shading (and disabling) calendar cells whose time has already
// passed. The caller computes the viewer-local parts of "now" ONCE per render
// (instantToLocalParts) and passes them here; comparing wall-clock day/hour
// numbers directly is cheap (no per-cell timezone math) and DST-correct — the
// ambiguous fall-back hour is treated as "current" until the wall clock ticks
// past it, rather than an instant offset that misfires across the transition.

/** The viewer-local "now" fields these helpers compare against. */
export interface NowParts {
  /** "YYYY-MM-DD" in the viewer's timezone. */
  dateKey: string;
  /** 0–23 wall-clock hour in the viewer's timezone. */
  hour: number;
}

/** True when the whole day `dateKey` is before today (used for month cells). */
export function isPastDay(dateKey: string, now: NowParts): boolean {
  return dateKey < now.dateKey;
}

/** True when the [hour, hour+1) slot on `dateKey` has fully elapsed. The
 *  in-progress hour is not considered past. */
export function isPastHour(dateKey: string, hour: number, now: NowParts): boolean {
  if (dateKey < now.dateKey) return true;
  if (dateKey > now.dateKey) return false;
  return hour < now.hour;
}

/** Snap a vertical cursor offset within an hour cell to a 15-minute quarter
 *  (0, 15, 30, or 45). `offsetY` is the cursor's distance from the cell top;
 *  `height` is the cell height. */
export function minuteFromOffset(offsetY: number, height: number): number {
  if (height <= 0) return 0;
  const quarter = Math.floor((offsetY / height) * 4);
  return Math.min(3, Math.max(0, quarter)) * 15;
}
